// src/pages/admin/analytics/components/DeleteWastageModal.jsx
import { useState } from 'react';
import { X, Trash2, AlertTriangle, RefreshCw, ArrowUpRight, Package, Building2, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import analyticsService from '../../../../services/analyticsService';

const DeleteWastageModal = ({ isOpen, onClose, onSuccess, log, bulkLogs = [], onClearSelection }) => {
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const isBulk = Array.isArray(bulkLogs) && bulkLogs.length > 0;

  const handleDelete = async () => {
    try {
      setLoading(true);

      if (isBulk) {
        // Delete all selected logs sequentially
        for (const item of bulkLogs) {
          await analyticsService.deleteWastageEntry(item._id);
        }
        toast.success(`Successfully deleted ${bulkLogs.length} wastage entries and restored stock!`);
        if (onClearSelection) onClearSelection();
      } else if (log) {
        await analyticsService.deleteWastageEntry(log._id);
        toast.success('Wastage entry deleted and stock restored successfully!');
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to delete wastage entry:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to delete wastage record');
    } finally {
      setLoading(false);
    }
  };

  const isRM = log?.itemType === 'raw_material';
  const itemName = isRM ? log?.rawMaterial?.name : log?.product?.name;
  const itemCode = isRM ? log?.rawMaterial?.code : log?.product?.sku;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="p-6 pb-4 flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center flex-shrink-0 text-red-600">
              <Trash2 className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-black text-gray-900">
                {isBulk ? `Delete ${bulkLogs.length} Wastage Entries?` : 'Delete Wastage Entry?'}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {isBulk ? 'Bulk record deletion & stock recovery' : 'Record deletion & inventory recovery'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="p-1.5 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-2 space-y-4">
          {/* Detailed Item Card (Single mode) */}
          {!isBulk && log && (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 text-xs">
              <div className="flex items-start justify-between">
                <div>
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold mb-1 ${
                      isRM ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}
                  >
                    {isRM ? 'Raw Material' : 'Product'}
                  </span>
                  <h4 className="text-sm font-bold text-gray-900">{itemName || 'Item Details'}</h4>
                  {itemCode && <p className="text-[10px] text-gray-500">Code/SKU: {itemCode}</p>}
                </div>
                <div className="text-right">
                  <span className="text-xs font-black text-rose-700 block">
                    {log.quantity} {log.unitName || 'units'}
                  </span>
                  <span className="text-[11px] font-bold text-gray-700">
                    Rs. {(log.totalCost || 0).toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="mt-3 pt-2.5 border-t border-gray-200 flex items-center justify-between text-[11px] text-gray-500">
                <div className="flex items-center space-x-1">
                  <Building2 className="h-3 w-3 text-gray-400" />
                  <span className="truncate max-w-[130px]">{log.branch?.name || 'All Branches'}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Calendar className="h-3 w-3 text-gray-400" />
                  <span>
                    {new Date(log.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Bulk summary card */}
          {isBulk && (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 text-xs space-y-1.5">
              <div className="flex justify-between font-bold text-gray-800">
                <span>Selected Records:</span>
                <span className="text-rose-600">{bulkLogs.length} items</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Total Wastage Value:</span>
                <span className="font-bold text-gray-900">
                  Rs.{' '}
                  {bulkLogs
                    .reduce((sum, item) => sum + (item.totalCost || 0), 0)
                    .toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}

          {/* Inventory Stock Restoration Alert */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3.5 flex items-start space-x-2.5">
            <ArrowUpRight className="h-4 w-4 text-emerald-700 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-emerald-900 leading-relaxed">
              <span className="font-bold">Inventory will be restored:</span>{' '}
              {!isBulk && log ? (
                <>
                  Deducted quantity of{' '}
                  <span className="font-black text-emerald-950">
                    {log.quantity} {log.unitName || 'units'}
                  </span>{' '}
                  will be credited back to{' '}
                  <span className="font-bold">{log.branch?.name || 'the branch'}</span> stock.
                </>
              ) : (
                <>
                  All deducted quantities across {bulkLogs.length} items will be automatically credited
                  back to inventory.
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 pt-4 flex items-center justify-end space-x-3 bg-gray-50 border-t border-gray-100 mt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2.5 text-xs font-bold text-gray-600 hover:bg-gray-200 rounded-xl transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={loading}
            className="px-5 py-2.5 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white text-xs font-bold rounded-xl shadow-md hover:shadow-lg transition-all flex items-center space-x-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Deleting...</span>
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                <span>Delete & Restore Stock</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteWastageModal;
